/* exporters.ts — download tickets in tracker-ready formats. */

import type { Ticket } from './types';
import { sevMeta, statusMeta } from './constants';

export type ExportKind = 'json' | 'markdown' | 'linear' | 'github' | 'jira';

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 1000);
}

function csvCell(v: unknown): string {
  let s = v == null ? '' : String(v);
  // Neutralize CSV formula injection: Excel/Sheets execute cells that lead with = + - @ (or tab/CR).
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return /["\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function screenLine(t: Ticket): string {
  return `${t.route || '—'}`;
}

function exportJSON(tickets: Ticket[]): void {
  download(
    new Blob([JSON.stringify(tickets, null, 2)], { type: 'application/json' }),
    `tickets-${dateStamp()}.json`,
  );
}

const STATUS_ORDER = ['open', 'triaged', 'in-progress', 'done', 'wontfix'];

function exportMarkdown(tickets: Ticket[]): void {
  let md = `# Ticket Export\n\n_Exported ${new Date().toLocaleString()} · ${tickets.length} tickets_\n\n`;
  for (const status of STATUS_ORDER) {
    const group = tickets.filter((t) => t.status === status);
    if (!group.length) continue;
    md += `## ${statusMeta(status).label} (${group.length})\n\n`;
    for (const t of group) {
      md += `### \`${t.id}\` · [${t.tag.toUpperCase()}] ${t.title}\n`;
      md += `- **Severity**: ${sevMeta(t.severity).label}\n`;
      md += `- **Screen**: \`${screenLine(t)}\`${t.routeLabel ? ` · ${t.routeLabel}` : ''}\n`;
      md += `- **Created**: ${new Date(t.createdAt).toLocaleString()} · ${t.viewport || '—'}\n`;
      if (t.assignee) md += `- **Assignee**: ${t.assignee}\n`;
      md += `\n${t.note}\n\n`;
      if ((t.comments || []).length) {
        md += `**Comments:**\n\n`;
        for (const c of t.comments) md += `> _${c.who} · ${new Date(c.at).toLocaleString()}_\n> ${c.text}\n\n`;
      }
      md += `---\n\n`;
    }
  }
  download(new Blob([md], { type: 'text/markdown' }), `tickets-${dateStamp()}.md`);
}

const LINEAR_PRIORITY: Record<string, string> = { low: '4', medium: '3', high: '2', blocker: '1' };
const LINEAR_STATUS: Record<string, string> = {
  open: 'Backlog',
  triaged: 'Todo',
  'in-progress': 'In Progress',
  done: 'Done',
  wontfix: 'Cancelled',
};

function descBlock(t: Ticket): string {
  return `${t.note}\n\nScreen: ${t.route || '—'}\nReported: ${new Date(
    t.createdAt,
  ).toLocaleString()}\nViewport: ${t.viewport || '—'}\nTicket ID: ${t.id}`;
}

function labels(t: Ticket): string[] {
  return [t.tag, 'screen:' + (t.route || 'unknown'), 'severity:' + t.severity].filter(Boolean);
}

function exportLinearCSV(tickets: Ticket[]): void {
  const rows = [['Title', 'Description', 'Priority', 'Status', 'Labels', 'Estimate']];
  for (const t of tickets) {
    rows.push([
      `[${t.tag.toUpperCase()}] ${t.title}`,
      descBlock(t),
      LINEAR_PRIORITY[t.severity] || '0',
      LINEAR_STATUS[t.status] || 'Backlog',
      labels(t).join(','),
      '',
    ]);
  }
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  download(new Blob([csv], { type: 'text/csv' }), `tickets-linear-${dateStamp()}.csv`);
}

function exportGithub(tickets: Ticket[]): void {
  let md = `# GitHub Issues — paste each block as a new issue\n\n`;
  tickets.forEach((t, i) => {
    md += `<!-- ── ISSUE ${i + 1} ─────────────────────────────────────── -->\n`;
    md += `## Title\n[${t.tag.toUpperCase()}] ${t.title}\n\n`;
    md += `## Body\n${t.note}\n\n`;
    md += `**Reproduction context**\n`;
    md += `- Screen: \`${t.route || '—'}\`\n`;
    md += `- Severity: **${sevMeta(t.severity).label}**\n`;
    md += `- Reported: ${new Date(t.createdAt).toLocaleString()}\n`;
    md += `- Viewport: ${t.viewport || '—'}\n`;
    md += `- Ticket ID: \`${t.id}\`\n\n`;
    md += `**Labels**: \`${t.tag}\`, \`severity:${t.severity}\`, \`screen:${t.route || 'unknown'}\`\n\n`;
  });
  download(new Blob([md], { type: 'text/markdown' }), `tickets-github-${dateStamp()}.md`);
}

const JIRA_TYPE: Record<string, string> = {
  bug: 'Bug',
  idea: 'Story',
  comment: 'Task',
  praise: 'Task',
  question: 'Task',
};
const JIRA_PRIORITY: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  blocker: 'Highest',
};
const JIRA_STATUS: Record<string, string> = {
  open: 'To Do',
  triaged: 'To Do',
  'in-progress': 'In Progress',
  done: 'Done',
  wontfix: 'Closed',
};

function exportJiraCSV(tickets: Ticket[]): void {
  const rows = [
    ['Summary', 'Issue Type', 'Priority', 'Status', 'Description', 'Labels', 'Reporter'],
  ];
  for (const t of tickets) {
    rows.push([
      `[${t.tag.toUpperCase()}] ${t.title}`,
      JIRA_TYPE[t.tag] || 'Task',
      JIRA_PRIORITY[t.severity] || 'Medium',
      JIRA_STATUS[t.status] || 'To Do',
      descBlock(t),
      labels(t).join(' '),
      t.assignee || '',
    ]);
  }
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  download(new Blob([csv], { type: 'text/csv' }), `tickets-jira-${dateStamp()}.csv`);
}

export function runExport(kind: ExportKind, tickets: Ticket[]): void {
  switch (kind) {
    case 'json':
      return exportJSON(tickets);
    case 'markdown':
      return exportMarkdown(tickets);
    case 'linear':
      return exportLinearCSV(tickets);
    case 'github':
      return exportGithub(tickets);
    case 'jira':
      return exportJiraCSV(tickets);
  }
}
